
import AppSendModal from "@/components/sales/SaleSendModal.vue";
import { ToolbarService } from "@/services/toolbar-service";
import Vue from "vue";
import { Sale } from "@/models/base";
import { SaleLayoutConf } from "../../models/sale-layout";
import { SaleService } from "../../services/sale-service";
import { UserConfigService } from "../../services/user-config-service";
import { AuthService } from "../../services/auth-service";
import { MODULE } from "../../config/modules";
import { UblService } from "../../plugins/ubl/services/ubl-service";
import AppPublicLink from "@/components/PublicLink.vue";
import AppSaleInfoOverview from "@/components/sales/SaleInfoOverview.vue";
import AppSalePdfPreview from "@/components/sales/SalePdfPreview.vue";
import AppSaleHistory from "@/components/sales/SaleHistory.vue";
import AppSaleReferences from "@/components/sales/SaleReferences.vue";
import AppSaleInfoAwsDocuments from "@/components/sales/SaleInfoAwsDocuments.vue";
import { TranslateResult } from "vue-i18n";
import { ISelectItem } from "winfakt-vue-components";
import { PrintContext, SalePrintSettings } from "@/utils/sale-layouts/print-settings";
import AppSignSaleModal from "@/components/sales/SignSaleModal.vue";
import AppSalePrintExtraOptionsModal from "@/components/sales/SalePrintExtraOptionsModal.vue";
import AppSendViaPeppolModal from "@/components/sales/SendViaPeppolModal.vue";
import AppCustomViewTabFrames from "@/components/browse/CustomViewTabFrames.vue";
import { LoggerService, LogType } from "@/services/logger-service";
import { ExactSyncService } from "@/services/exact-sync-service";
import { ClearfactsSyncService } from "@/services/clearfacts-sync-service";
import { tokenStore } from "@/utils/token-store";
import { BrowserInfo } from "@/utils/browser";

export default Vue.extend({
	data() {
		return {
			layout: new SaleLayoutConf(),
			sale: new Sale(),
			loaded: false,
			sendModal: false,
			sendWithExtraPrintOptions: false,
			importReferencedSales: [] as Sale[],
			publicLinkModal: false,
			publicLink: "",
			activeTab: 0,
			signModalOpen: false,
			pdfKey: 0,
			printWithExtraOptionsModalOpen: false,
			peppolModalOpen: false,
			isPrinting: false,
			isDownloading: false,
			isPushingToExact: false,
			isPushingToClearfacts: false,
			ready: false
		};
	},
	props: {
		saleId: Number
	},
	computed: {
		locale(): string {
			return this.$i18n.locale;
		},
		canEdit(): boolean {
			if (!UserConfigService.hasCurrentUserPermission((p) => p.SalePermissions.hasWritePermissions())) {
				return false;
			}

			if (!UserConfigService.hasCurrentUserPermission((p) => p.canManageSaleType(this.sale.TypeID))) {
				return false;
			}

			if (this.sale.IsSigned) {
				return false;
			}

			return true;
		},
		canDownloadUbl(): boolean {
			if (!AuthService.hasModule(MODULE.ELECTRONISCHE_FACTURATIE)) {
				return false;
			}
			if (this.sale.TypeID > 2) {
				return false;
			}
			return true;
		},
		canBeSignedByClient(): boolean{
			return this.sale.canBeSignedByClient();
		},
		tabs(): TranslateResult[] {
			let tabs = Object.values(this.$ct("InfoTabs"));
			if (this.$wf.isMdUp) {
				tabs.push(this.$ct("documents"));
			} else {
				tabs.push(this.$ct("extra-info"));
			}
			let customTabNames = UserConfigService.getCustomViewTabs().getTabNamesForBrowse(6);
			return [...tabs, ...customTabNames];
		},
		customViewTabs() {
			return UserConfigService.getCustomViewTabsForBrowse(6);
		},
		customViewTabStartIndex(): number {
			// InfoTabs (2) + documents/extra-info tab (1) = 3
			return Object.values(this.$ct("InfoTabs")).length + 1;
		},
		title(): TranslateResult {
			return ToolbarService.title;
		},
		// eslint-disable-next-line max-lines-per-function
		toolbarActions(): ISelectItem<Function>[] {
			let result:ISelectItem<Function>[] = [
				{
					text: this.$ct("common.send"),
					value: this.openSendModal,
					icon: "envelope",
				}
			];
			if (this.$wf.isMdDown) {
				result.push(...this.extraSendActions);
			}
			result.push(
				{
					text: this.$ct("common.download"),
					value: this.download,
					icon: "download",
					disabled: this.isDownloading
				},
				{
					text: this.$ct("common.print"),
					value: this.print,
					icon: "print",
					disabled: this.isPrinting
				},
			);
			if (this.$wf.isMdDown) {
				result.push(...this.extraPrintActions);
			}
			result.push({
					text: this.$ct("public-url-create"),
					value: this.createPublicLink,
					icon: "link"
				},
				{
					text: this.$ct("download-ubl"),
					value: this.downloadUbl,
					icon: "file-invoice"
				}
			);
			if (this.isAccountant) {
				result.push({
					text: this.$ct("push-to-exact"),
					value: this.pushToExact,
					icon: "cloud-upload-alt",
					disabled: this.isPushingToExact
				});
				result.push({
					text: this.$ct("push-to-clearfacts"),
					value: this.pushToClearfacts,
					icon: "cloud-upload-alt",
					disabled: this.isPushingToClearfacts
				});
			}
			return result;
		},
		isAccountant():boolean {
			return tokenStore.isAccountant;
		},
		extraPrintActions():ISelectItem<Function>[] {
			return [
				/*{
					text: this.$ct("print-duplicate"),
					value: this.printDuplicate,
					icon: "fax"
				},*/
				{
					text: this.$ct("print-with-extra-options"),
					value: this.openPrintExtraOptionsModal,
					icon: "cog"
				},
			];
		},
		extraSendActions():ISelectItem<Function>[]{
			return [
				{
					text: this.$ct("send-with-extra-options"),
					value: this.openSendExtraOptionsModal,
					icon: "cog"
				},
				{
					text: this.$ct("peppol-send"),
					value: this.sendViaPeppol,
					icon: "project-diagram"
				}
			];
		}
	},
	watch: {
		"$wf.isMdUp": {
			immediate: true,
			handler() {
				if (!this.$wf.isXl && this.$wf.isMdUp && this.activeTab > 2) {
					this.activeTab = 2;
				}
			}
		},
		"$wf.isXl": {
			immediate: true,
			handler() {
				if (this.$wf.isXl && this.activeTab > 1) {
					this.activeTab = 0;
				}
			}
		}
	},
	methods: {
		onSign(s: Sale){
			this.sale = s;
			this.pdfKey++;
		},
		back() {
			if (history.length <= 1){
				if (BrowserInfo.isElectron()){
					window.close();
				}else{
					this.$router.push("/sales");
				}
			}else{
				this.$router.back();
			}
		},
		edit() {
			if(this.saleId){
				this.$emit("edit");
			}else{
				this.$router.push(`/sales/edit/${this.sale.ID}`);
			}
		},
		openPrintExtraOptionsModal(){
			this.printWithExtraOptionsModalOpen = true;
		},
		openSendExtraOptionsModal(){
			this.sendModal = true;
			this.sendWithExtraPrintOptions = true;
		},
		openSendModal() {
			this.sendModal = true;
			this.sendWithExtraPrintOptions = false;
		},
		async download() {
			try{
				this.isDownloading = true;
				await SaleService.downloadAndSaveSale(this.sale, this.layout);
				await this.refreshHistory();
			}finally{
				this.isDownloading = false;
			}
		},
		async printDuplicate(){
			await this.print(true);
		},
		async print(isDuplicate:boolean = false) {
			try{
				this.isPrinting = true;
				let settings = new SalePrintSettings(PrintContext.PRINT);
				settings.isDuplicate = isDuplicate;
				await SaleService.printSale(this.sale, settings.layoutId, settings);
				await this.refreshHistory();
			}catch(err){
				let error = err as Error;
				let message = `[SaleInfo] Failed to print. | ${error.message} | ${error.cause || ""}`;
				LoggerService.createLog(message, error.stack || "", LogType.ERROR);
				throw err;
			} finally{
				this.isPrinting = false;
			}

		},
		async refreshHistory() {
			this.sale.History = await SaleService.getSaleHistoryBySaleId(this.sale.ID);
		},
		mailSent() {
			this.sendModal = false;
			this.refreshHistory();
		},
		async downloadUbl() {
			await UblService.downloadInvoiceUbl(this, this.sale);
		},
		async pushToClearfacts() {
			if (this.isPushingToClearfacts) return;
			this.isPushingToClearfacts = true;
			try {
				await ClearfactsSyncService.pushSale(this.sale.ID);
				this.$wf.notify(this.$ct("push-to-clearfacts-success").toString(), "success", 4000, "check");
			} catch (err) {
				let msg = (err as any)?.message || "";
				this.$wf.notify(this.$ct("push-to-clearfacts-failed", {error: msg}).toString(), "error", 6000, "exclamation-triangle");
			} finally {
				this.isPushingToClearfacts = false;
			}
		},
		async pushToExact() {
			if (this.isPushingToExact) return;
			this.isPushingToExact = true;
			try {
				let res = await ExactSyncService.pushSale(this.sale.ID);
				this.$wf.notify(this.$ct("push-to-exact-success", {id: res.InvoiceID}).toString(), "success", 4000, "check");
				if (res.AttachmentError) {
					this.$wf.notify(this.$ct("push-to-exact-attachment-failed", {error: res.AttachmentError}).toString(), "warning", 8000, "exclamation-triangle");
				}
			} catch (err) {
				let msg = (err as any)?.message || "";
				this.$wf.notify(this.$ct("push-to-exact-failed", {error: msg}).toString(), "error", 6000, "exclamation-triangle");
			} finally {
				this.isPushingToExact = false;
			}
		},
		async fetchReferencedSales() {
			this.importReferencedSales = await SaleService.getReferencingImportSales(this.sale.ID);
		},
		openSale(saleId: number) {
			this.$router.push(`/sales/info/${saleId}`);
		},
		async createPublicLink() {
			this.publicLinkModal = true;
			if (!this.publicLink) {
				this.publicLink = await SaleService.generatePublicUrl(this.sale);
			}
		},
		async uploadAwsDocuments(){
			this.sale = await SaleService.uploadDocuments(this.sale);
		},
		sendViaPeppol(){
			this.peppolModalOpen = true;
		}
	},
	async created() {
		let id = parseInt(this.$route.params.id || "0");
		if (this.saleId) {
			id = this.saleId;
		}
		this.sale = await SaleService.getSale(id);
		this.layout = await SaleService.getSaleLayout(this.sale.getLayoutID(PrintContext.DOWNLOAD));
		this.loaded = true;
		this.fetchReferencedSales();

		let locale = this.$i18n.locale;
		let typeName = this.sale.Type.getDisplayedName(locale);

		ToolbarService.title = typeName + " " + this.sale.ComputedFriendlyID;
		this.ready = true;
	},
	components: {
		AppSendModal,
		AppPublicLink,
		AppSaleInfoOverview,
		AppSalePdfPreview,
		AppSaleHistory,
		AppSaleReferences,
		AppSaleInfoAwsDocuments,
		AppSignSaleModal,
		AppSalePrintExtraOptionsModal,
		AppSendViaPeppolModal,
		AppCustomViewTabFrames
	},

	i18nEx: {
		componentPrefix: "components.sales"
	}
});
